/**
 * 取消预约限制管理工具
 * 规则：一天内取消3次以上，则当天不能再进行预约操作
 */

const STORAGE_KEY = 'cancel_reserve_records';
const MAX_CANCEL_COUNT = 3; // 最大取消次数

/**
 * 获取今天的日期字符串 (YYYY-MM-DD)
 */
function getTodayString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 获取用户今天的取消记录
 * @param {string} idcard - 用户身份证号
 * @returns {object} - { date, count, timestamps }
 */
function getTodayCancelRecord(idcard) {
  if (!idcard) return null;
  
  try {
    const records = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const userRecords = records[idcard] || {};
    const today = getTodayString();
    
    // 如果记录不是今天的，返回空记录
    if (userRecords.date !== today) {
      return { date: today, count: 0, timestamps: [] };
    }
    
    return userRecords;
  } catch (error) {
    console.error('获取取消记录失败:', error);
    return { date: getTodayString(), count: 0, timestamps: [] };
  }
}

/**
 * 记录一次取消操作
 * @param {string} idcard - 用户身份证号
 * @returns {number} - 返回今天的取消次数
 */
export function recordCancelAction(idcard) {
  if (!idcard) return 0;
  
  try {
    const records = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const today = getTodayString();
    const now = new Date().getTime();
    
    // 获取或初始化用户记录
    let userRecord = records[idcard] || { date: today, count: 0, timestamps: [] };
    
    // 如果不是今天的记录，重置
    if (userRecord.date !== today) {
      userRecord = { date: today, count: 0, timestamps: [] };
    }
    
    // 增加取消次数
    userRecord.count += 1;
    userRecord.timestamps.push(now);
    
    // 保存记录
    records[idcard] = userRecord;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    
    return userRecord.count;
  } catch (error) {
    console.error('记录取消操作失败:', error);
    return 0;
  }
}

/**
 * 检查用户今天是否还能预约
 * @param {string} idcard - 用户身份证号
 * @returns {object} - { canReserve: boolean, cancelCount: number, message: string }
 */
export function checkCanReserve(idcard) {
  if (!idcard) {
    return { canReserve: true, cancelCount: 0, message: '' };
  }
  
  const record = getTodayCancelRecord(idcard);
  const cancelCount = record.count;
  
  if (cancelCount >= MAX_CANCEL_COUNT) {
    return {
      canReserve: false,
      cancelCount: cancelCount,
      message: `您今天已取消${cancelCount}次，已达上限，今天无法预约，请明天再试。`
    };
  }
  
  return {
    canReserve: true,
    cancelCount: cancelCount,
    message: ''
  };
}

/**
 * 获取用户今天的取消次数
 * @param {string} idcard - 用户身份证号
 * @returns {number} - 取消次数
 */
export function getTodayCancelCount(idcard) {
  if (!idcard) return 0;
  const record = getTodayCancelRecord(idcard);
  return record.count;
}

/**
 * 获取剩余可取消次数
 * @param {string} idcard - 用户身份证号
 * @returns {number} - 剩余可取消次数
 */
export function getRemainingCancelCount(idcard) {
  const count = getTodayCancelCount(idcard);
  return Math.max(0, MAX_CANCEL_COUNT - count);
}

/**
 * 清除用户的取消记录（仅用于测试或管理）
 * @param {string} idcard - 用户身份证号
 */
export function clearCancelRecord(idcard) {
  if (!idcard) return;
  
  try {
    const records = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    delete records[idcard];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch (error) {
    console.error('清除取消记录失败:', error);
  }
}
